import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { connect } from "react-redux";
import { QrReader } from "react-qr-reader";
import { getQRDetailsUsingScanner } from "../../action/qrcode";

const QRScanner = ({ qrCodeScanDetailsHandler }) => {
    const [cameraError, setCameraError] = useState("");
    const [hasVideo, setHasVideo] = useState(false);
    const qrContainerRef = useRef(null);

    const isIgnorableScannerError = (error) => {
        if (!error) return true;
        const errorMessage = (error.message || error.name || String(error)).toLowerCase();
        return (
            errorMessage.includes("notfound") ||
            errorMessage.includes("timeout") ||
            errorMessage.includes("no qr code") ||
            errorMessage.includes("no code found")
        );
    };

    const mapCameraError = (error) => {
        if (!error) return "";
        const errorName = error?.name || "";
        const errorMessage = error?.message || "";
        if (["NotAllowedError", "SecurityError"].includes(errorName)) {
            return "Camera permission is blocked. Please allow access to start scanning.";
        }
        if (["NotFoundError", "OverconstrainedError"].includes(errorName)) {
            return "No compatible camera found. Connect a camera and try again.";
        }
        if (["NotReadableError", "TrackStartError"].includes(errorName)) {
            return "Camera is in use or unavailable. Close other apps using the camera.";
        }
        if (errorMessage) {
            return "Unable to access the camera. Please check permissions and retry.";
        }
        return "";
    };

    const handleScanResult = (data) => {
        if (data) {
            getQRDetailsUsingScanner(data.text)
                .then((resp) => {
                    setCameraError("");
                    qrCodeScanDetailsHandler(resp);
                })
                .catch((error) => {
                    setCameraError("");
                    qrCodeScanDetailsHandler(undefined, error);
                });
        }
    };

    useEffect(() => {
        const container = qrContainerRef.current;
        if (!container) return;

        const video = container.querySelector("video");
        if (!video) return;

        const handleVideoStart = () => {
            setHasVideo(true);
            setCameraError("");
        };

        video.addEventListener("loadeddata", handleVideoStart);
        video.addEventListener("playing", handleVideoStart);

        return () => {
            video.removeEventListener("loadeddata", handleVideoStart);
            video.removeEventListener("playing", handleVideoStart);
        };
    }, []);

    return (
        <Box
            ref={qrContainerRef}
            sx={{
                width: "100%",
                minWidth: 0,
                maxWidth: { xs: "100%", sm: "100%", md: 500 },
                height: { xs: 240, sm: 320, md: 400 },
                mt: 0,
                position: "relative",
                boxSizing: "border-box",
            }}
        >
            <QrReader
                constraints={{ facingMode: { ideal: "environment" } }}
                onResult={(result, error) => {
                    if (result) {
                        setCameraError("");
                        handleScanResult(result);
                        return;
                    }
                    if (error) {
                        if (isIgnorableScannerError(error)) return;
                        setCameraError(mapCameraError(error));
                    } else {
                        setCameraError("");
                    }
                }}
                containerStyle={{
                    width: "100%",
                    height: "100%",
                }}
                videoContainerStyle={{ paddingTop: "80%" }}
                videoStyle={{
                    width: "100%",
                    height: "100%",
                    transform: "none",
                }}
            />
            {cameraError && !hasVideo && (
                <Box
                    role="status"
                    aria-live="polite"
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        bgcolor: "rgba(255, 255, 255, 0.88)",
                        color: "#333",
                        textAlign: { xs: "center", sm: "left" },
                        p: { xs: 1.5, sm: 2 },
                        pl: { xs: 1.5, sm: 0 },
                        borderRadius: 1,
                        fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                        lineHeight: 1.4,
                        hyphens: "none",
                        wordBreak: "normal",
                        overflowWrap: "break-word",
                        maxWidth: "100%",
                        maxHeight: "100%",
                        overflowY: "auto",
                        boxSizing: "border-box",
                    }}
                >
                    {cameraError}
                </Box>
            )}
        </Box>
    );
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps)(QRScanner);
